import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import LayoutWrapper from "../../../components/LayoutWrapper";
import HeaderBar from "../../../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { ThemeContext } from "../../../context/ThemeContext";
import getCurrentSkin from "../../../utils/getCurrentSkin";
import {
  ArrowLeft,
  History,
  X,
  Plus,
  Search,
  CheckCircle,
  Building2,
  ClipboardList,
  Package,
  Users,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com/api";

// ─── ItemHistoryModal ───────────────────────────────────────────────────────
const ItemHistoryModal = ({ itemId, itemName, onClose, commonHeaders, isDark, baseColor }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/getItemHistory/${itemId}`, {
        headers: commonHeaders,
      })
      .then((res) => setHistory(res.data.history || []))
      .catch(() => toast.error("Failed to load item history"))
      .finally(() => setLoading(false));
  }, [itemId, commonHeaders]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div
        className={`rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden ${
          isDark ? "bg-slate-900 border border-slate-700" : "bg-white"
        }`}
      >
        <div
          className={`flex items-center justify-between px-5 py-4 bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
        >
          <div className="flex items-center gap-2">
            <History size={18} />
            <span className="font-bold text-sm">Item History — {itemName}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/20 transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-auto flex-1 p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No history found</div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-700">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">S.No</th>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">Quotation No</th>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">Supplier</th>
                    <th className="px-3 py-2 text-center text-xs font-bold uppercase">Qty</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase">Unit Price</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? "divide-slate-700" : "divide-gray-200"}`}>
                  {history.map((h, i) => (
                    <tr
                      key={i}
                      className={
                        isDark
                          ? i % 2 === 0
                            ? "bg-slate-800/40"
                            : "bg-slate-800/20"
                          : i % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50"
                      }
                    >
                      <td className="px-3 py-2 text-center">{i + 1}</td>
                      <td className="px-3 py-2">{h.fld_QuotationNo}</td>
                      <td className="px-3 py-2">
                        {h.fld_QuotationDate
                          ? new Date(h.fld_QuotationDate).toLocaleDateString()
                          : ""}
                      </td>
                      <td className="px-3 py-2">{h.fld_suppliername}</td>
                      <td className="px-3 py-2 text-center">
                        {h.fld_qty || h.fld_Quantity || ""}
                      </td>
                      <td className="px-3 py-2 text-right">{h.fld_unitprice || ""}</td>
                      <td className="px-3 py-2 text-right">{h.fld_totalamt || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────
const RateComparisonAdd = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const loginuserid = Number(userId) || 0;
  const fld_branchid = Number(branchId) || 0;

  const normalizedBranch = String(branch || "").trim().toUpperCase();
  const isHO =
    normalizedBranch === "HO" ||
    normalizedBranch === "HEAD-OFFICE" ||
    normalizedBranch === "HEAD OFFICE";

  const initialBranchType = isHO ? "HO" : "B";
  const [branchType, setBranchType] = useState(initialBranchType);

  const [requisitions, setRequisitions] = useState([]);
  const [selectedReqIds, setSelectedReqIds] = useState([]);
  const [reqSelectAll, setReqSelectAll] = useState(false);
  const [loadingReqs, setLoadingReqs] = useState(false);

  const [itemData, setItemData] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [itemSelectAll, setItemSelectAll] = useState(false);
  const [unmatchedItemNames, setUnmatchedItemNames] = useState([]);
  const [currentRequestId, setCurrentRequestId] = useState(null);

  const [supplierData, setSupplierData] = useState([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [currentItemIds, setCurrentItemIds] = useState(null);

  const [historyModal, setHistoryModal] = useState(null);

  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingValidate, setLoadingValidate] = useState(false);

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const fetchRequisitions = useCallback(() => {
    setLoadingReqs(true);
    const reqtype = branchType === "HO" ? "H" : "B";

    axios
      .get(`${BASE_URL}/getRateComparison`, {
        params: {
          branchid: fld_branchid,
          reqtype,
          branchtype: branchType,
          loginuserid,
        },
        headers: commonHeaders,
      })
      .then((res) => {
        setRequisitions(res.data.rows || []);
        setSelectedReqIds([]);
        setReqSelectAll(false);
        setItemData([]);
        setSelectedItemIds([]);
        setSupplierData([]);
        setSelectedSupplierIds([]);
        setUnmatchedItemNames([]);
        setCurrentRequestId(null);
        setCurrentItemIds(null);
      })
      .catch(() => toast.error("Failed to load requisitions"))
      .finally(() => setLoadingReqs(false));
  }, [branchType, fld_branchid, loginuserid, commonHeaders]);

  useEffect(() => {
    fetchRequisitions();
  }, [fetchRequisitions]);

  const toggleReqRow = (id) => {
    setSelectedReqIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleReqSelectAll = () => {
    if (reqSelectAll) {
      setSelectedReqIds([]);
    } else {
      setSelectedReqIds(requisitions.map((r) => r.fld_purchaserequestid));
    }
    setReqSelectAll(!reqSelectAll);
  };

  useEffect(() => {
    if (requisitions.length > 0 && selectedReqIds.length === requisitions.length) {
      setReqSelectAll(true);
    } else {
      setReqSelectAll(false);
    }
  }, [selectedReqIds, requisitions]);

  const handleAddRequisitions = () => {
    if (selectedReqIds.length === 0) {
      toast.warn("Please select at least one Purchase Requisition");
      return;
    }

    setLoadingItems(true);
    setItemData([]);
    setSelectedItemIds([]);
    setItemSelectAll(false);
    setSupplierData([]);
    setSelectedSupplierIds([]);
    setUnmatchedItemNames([]);

    axios
      .post(
        `${BASE_URL}/loadRateComparison`,
        {
          selectedRequestIds: selectedReqIds,
          loginuserid,
          branchid: fld_branchid,
        },
        { headers: commonHeaders }
      )
      .then((res) => {
        setItemData(res.data.rateComparisonItems || []);
        setUnmatchedItemNames(res.data.unmatchedItemNames || []);
        setCurrentRequestId(res.data.requestid);
      })
      .catch(() => toast.error("Failed to load request items"))
      .finally(() => setLoadingItems(false));
  };

  const toggleItemRow = (id) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleItemSelectAll = () => {
    if (itemSelectAll) {
      setSelectedItemIds([]);
    } else {
      setSelectedItemIds(itemData.map((i) => i.fld_itemid));
    }
    setItemSelectAll(!itemSelectAll);
  };

  useEffect(() => {
    if (itemData.length > 0 && selectedItemIds.length === itemData.length) {
      setItemSelectAll(true);
    } else {
      setItemSelectAll(false);
    }
  }, [selectedItemIds, itemData]);

  const handleViewSuppliers = () => {
    if (selectedItemIds.length === 0) {
      toast.warn("Please select at least one item");
      return;
    }

    setLoadingSuppliers(true);
    setSupplierData([]);
    setSelectedSupplierIds([]);

    axios
      .post(
        `${BASE_URL}/getSuppliersForSelectedItems`,
        {
          selectedItems: selectedItemIds,
          branchid: fld_branchid,
          requestid: currentRequestId,
          loginuserid,
        },
        { headers: commonHeaders }
      )
      .then((res) => {
        setSupplierData(res.data.newSuppliers || []);
        setCurrentItemIds(res.data.itemids);
        if ((res.data.newSuppliers || []).length === 0) {
          toast.info("No suppliers found for selected items");
        }
      })
      .catch(() => toast.error("Failed to load supplier data"))
      .finally(() => setLoadingSuppliers(false));
  };

  const toggleSupplierRow = (id) => {
    setSelectedSupplierIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleViewQuotation = () => {
    if (selectedSupplierIds.length === 0) {
      toast.warn("Please select at least one supplier for rate comparison");
      return;
    }
    if (selectedSupplierIds.length > 5) {
      toast.warn("Please select 1 to 5 suppliers for comparison");
      return;
    }

    setLoadingValidate(true);

    axios
      .post(
        `${BASE_URL}/selectSuppliersForComparison`,
        {
          itemids: currentItemIds
            ? currentItemIds.split(",").map((x) => x.trim())
            : selectedItemIds,
          requestids: selectedReqIds,
          selectedSuppliers: selectedSupplierIds,
          branchid: fld_branchid,
          loginuserid,
        },
        { headers: commonHeaders }
      )
      .then((res) => {
        if (res.data.success) {
          const p = res.data.redirect.params;
          navigate(
            `/inventory/QuotationEntryGST?pr=${p.pr}&su=${p.su}&itm=${p.itm}&s1=${p.s1}&s2=${p.s2}&s3=${p.s3}`
          );
        }
      })
      .catch((err) => {
        const msg =
          err.response?.data?.message || "Failed to validate suppliers";
        toast.error(msg);
      })
      .finally(() => setLoadingValidate(false));
  };

  const handleViewHistory = (itemId, itemName) => {
    setHistoryModal({ itemId, itemName });
  };

  const handleBranchTypeChange = (value) => {
    setBranchType(value);
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Rate Comparison</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      {historyModal && (
        <ItemHistoryModal
          itemId={historyModal.itemId}
          itemName={historyModal.itemName}
          onClose={() => setHistoryModal(null)}
          commonHeaders={commonHeaders}
          isDark={isDark}
          baseColor={baseColor}
        />
      )}

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Rate Comparison</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Select requisitions → items → suppliers for comparison
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
              isDark
                ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                : "border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>

        {/* Branch Type */}
        <div
          className={`p-5 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <label className={labelClass}>Branch Type</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleBranchTypeChange("HO")}
              disabled={!isHO}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                branchType === "HO"
                  ? `${buttonClass} ${hoverClass} text-white shadow`
                  : isDark
                  ? "bg-slate-800 text-slate-300 border border-slate-600"
                  : "bg-gray-100 text-gray-700 border border-gray-200"
              } ${!isHO ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Building2 size={16} />
              H.O
            </button>

            <button
              type="button"
              onClick={() => handleBranchTypeChange("B")}
              disabled={isHO}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                branchType === "B"
                  ? "bg-blue-600 text-white shadow"
                  : isDark
                  ? "bg-slate-800 text-slate-300 border border-slate-600"
                  : "bg-gray-100 text-gray-700 border border-gray-200"
              } ${isHO ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Branch
            </button>
          </div>
        </div>

        {/* Step 1 — Requisitions */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Step 1 — Select Purchase Requisitions</h3>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead
                className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
              >
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Request No / From Site</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Requisition Date</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Request For</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reqSelectAll}
                        onChange={toggleReqSelectAll}
                        className="w-4 h-4 rounded"
                      />
                      Select All
                    </label>
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {loadingReqs ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : requisitions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                      No Records Found!
                    </td>
                  </tr>
                ) : (
                  requisitions.map((item, index) => (
                    <tr
                      key={item.fld_purchaserequestid}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">{index + 1}</td>
                      <td className="px-4 py-3 font-medium">
                        {item.fld_purchaserequestno}
                      </td>
                      <td className="px-4 py-3">
                        {item.fld_Date
                          ? new Date(item.fld_Date).toLocaleDateString()
                          : item.fld_date}
                      </td>
                      <td className="px-4 py-3">{item.fld_indentref}</td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedReqIds.includes(item.fld_purchaserequestid)}
                          onChange={() => toggleReqRow(item.fld_purchaserequestid)}
                          className="w-4 h-4 rounded"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {requisitions.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center">
              <button
                onClick={handleAddRequisitions}
                disabled={loadingItems}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
              >
                <Plus size={16} />
                {loadingItems ? "Loading..." : "Add Selected Requisitions"}
              </button>
            </div>
          )}
        </div>

        {unmatchedItemNames.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 px-5 py-3 rounded-xl text-sm">
            <strong>Please add these items to supplier mapping:</strong>{" "}
            {unmatchedItemNames.join(", ")}
          </div>
        )}

        {/* Step 2 — Items */}
        {itemData.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <Package size={20} className={`text-${baseColor}-600`} />
                <h3 className="text-xl font-bold">Step 2 — Select Items for Rate Comparison</h3>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Name</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Code</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">HSN No</th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">Req Qty</th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={itemSelectAll}
                          onChange={toggleItemSelectAll}
                          className="w-4 h-4 rounded"
                        />
                        Select All
                      </label>
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                  {itemData.map((item, index) => (
                    <tr
                      key={item.fld_itemid}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">{index + 1}</td>
                      <td className="px-4 py-3">
                        <button
                          className={`inline-flex items-center gap-1.5 text-${baseColor}-600 hover:underline font-medium`}
                          onClick={() =>
                            handleViewHistory(item.fld_itemid, item.fld_itemname)
                          }
                          title="View Purchase History"
                        >
                          <History size={14} />
                          {item.fld_itemname}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-200"
                              : `bg-${baseColor}-100 text-${baseColor}-800`
                          }`}
                        >
                          {item.fld_itemcode}
                        </span>
                      </td>
                      <td className="px-4 py-3">{item.fld_itemPartno}</td>
                      <td className="px-4 py-3 text-center">{item.fld_qty}</td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedItemIds.includes(item.fld_itemid)}
                          onChange={() => toggleItemRow(item.fld_itemid)}
                          className="w-4 h-4 rounded"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center">
              <button
                onClick={handleViewSuppliers}
                disabled={loadingSuppliers}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
              >
                <Search size={16} />
                {loadingSuppliers ? "Loading..." : "View Suppliers for Selected Items"}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Suppliers */}
        {supplierData.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <Users size={20} className={`text-${baseColor}-600`} />
                <h3 className="text-xl font-bold">Step 3 — Select Suppliers (1 to 5)</h3>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Supplier Name</th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">Select</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                  {supplierData.map((item, index) => (
                    <tr
                      key={item.fld_supplierid}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">{index + 1}</td>
                      <td className="px-4 py-3 font-medium">{item.fld_suppliername}</td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedSupplierIds.includes(item.fld_supplierid)}
                          onChange={() => toggleSupplierRow(item.fld_supplierid)}
                          className="w-4 h-4 rounded"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {selectedSupplierIds.length} of {supplierData.length} supplier(s) selected
                {selectedSupplierIds.length > 5 && (
                  <span className="text-red-600 font-semibold ml-2">(max 5 allowed)</span>
                )}
              </div>

              <button
                onClick={handleViewQuotation}
                disabled={loadingValidate}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
              >
                <CheckCircle size={16} />
                {loadingValidate ? "Validating..." : "Proceed to Quotation Entry"}
              </button>
            </div>
          </div>
        )}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default RateComparisonAdd;